import { WidgetHealthConfig } from '../runtime/helpers';
import { store } from '../state/store';
import type {
  AuthStartResponse,
  AuthVerifyResponse,
  UploadResponse,
  HealthResponse,
  McpToolDef,
  TurnEventsPollResponse,
} from '../types';

function getBaseUrl(): string {
  return store.getState().config.serverUrl;
}

function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = store.getState().auth.token;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

function expireWidgetSession(message = 'Session expired. Please sign in again.'): never {
  store.setAuth({ status: 'unauthenticated', email: null, token: null });
  const error = new Error(message) as Error & { status?: number; isAuthExpired?: boolean };
  error.status = 401;
  error.isAuthExpired = true;
  throw error;
}

export function isWidgetAuthExpiredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { status?: number; isAuthExpired?: boolean; message?: string };
  if (candidate.status === 401 || candidate.isAuthExpired === true) return true;
  const message = candidate.message?.toLowerCase() ?? '';
  return message.includes('session expired')
    || message.includes('not authenticated')
    || message.includes('unauthorized');
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${getBaseUrl()}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: { ...getHeaders(), ...((init.headers as Record<string, string>) || {}) },
    credentials: 'include',
  });

  if (res.status === 401) {
    expireWidgetSession();
  }

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `${res.status} ${res.statusText}`);
  }

  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : (res.text() as unknown as T);
}

type SttApiError = Error & {
  status?: number;
  code?: number;
  responseMessage?: string;
};

export const webchatApi = {
  health(): Promise<HealthResponse> {
    const widgetId = store.getState().config.widgetId;
    const qs = widgetId ? `?widgetId=${encodeURIComponent(widgetId)}` : '';
    return request(`/webchat/health${qs}`);
  },

  getConfig(): Promise<WidgetHealthConfig> {
    const widgetId = store.getState().config.widgetId;
    const qs = widgetId ? `?widgetId=${encodeURIComponent(widgetId)}` : '';
    return request(`/webchat/config${qs}`);
  },

  authAnonymous(): Promise<AuthVerifyResponse> {
    return request('/webchat/auth/anonymous', { method: 'POST' });
  },

  authStart(email: string): Promise<AuthStartResponse> {
    const widgetId = store.getState().config.widgetId;
    return request('/webchat/auth/start', {
      method: 'POST',
      body: JSON.stringify({ email, ...(widgetId ? { widgetId } : {}) }),
    });
  },

  authVerify(email: string, code: string): Promise<AuthVerifyResponse> {
    return request('/webchat/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  },

  authLogout(): Promise<{ success: boolean }> {
    return request('/webchat/auth/logout', { method: 'POST' });
  },

  chat(
    messages: Array<{ role: string; content: string | unknown[]; tool_call_id?: string }>,
    tools?: McpToolDef[],
    conversationId?: string,
    clientChatId?: string,
    dangerousToolIds?: number[],
    sttUsed?: boolean,
    signal?: AbortSignal,
    clientRoundTrip?: number,
    clientTurnId?: string,
  ): Promise<Response> {
    const url = `${getBaseUrl()}/webchat/chat`;
    const headers: Record<string, string> = getHeaders();
    const payload: Record<string, unknown> = { messages };
    if (tools && tools.length > 0) {
      payload.tools = tools.map((t) => ({
        name: t.name,
        description: t.description,
        input_schema: t.inputSchema || { type: 'object', properties: {} },
      }));
      payload.clientToolExecution = true;
    }
    if (dangerousToolIds && dangerousToolIds.length > 0) {
      payload.dangerousTools = dangerousToolIds;
    }
    if (conversationId) payload.conversationId = conversationId;
    if (clientChatId) payload.clientChatId = clientChatId;
    const widgetId = store.getState().config.widgetId;
    if (widgetId) payload.widgetId = widgetId;
    payload.language = store.getState().config.language;
    if (store.getState().drivingMode.active) payload.drivingMode = true;
    if (sttUsed) payload.sttUsed = true;
    if (store.getState().drivingMode.active) payload.ttsUsed = true;
    const userGender = store.getState().drivingMode.userGender;
    if (userGender) payload.userGender = userGender;
    if (clientRoundTrip != null && clientRoundTrip > 0) payload.clientRoundTrip = clientRoundTrip;
    if (clientTurnId) payload.clientTurnId = clientTurnId;

    return fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(payload),
      signal,
    }).then((res) => {
      if (res.status === 401) {
        expireWidgetSession();
      }
      return res;
    });
  },

  /**
   * Long-poll a chat run's recorded events after `after` (server waits up to ~20s for new ones).
   * Fallback for when the /webchat/chat SSE response is held back by a buffering proxy.
   * Rejects with `status` (and `retryAfterMs` when the server sent Retry-After) on HTTP errors,
   * so callers can tell 404 (unknown run) and 429 (throttled) apart.
   */
  async pollTurnEvents(turnId: string, roundTrip: number, after: number, signal?: AbortSignal): Promise<TurnEventsPollResponse> {
    const qs = new URLSearchParams({ turnId, roundTrip: String(roundTrip), after: String(after) });
    const res = await fetch(`${getBaseUrl()}/webchat/chat/events?${qs}`, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
      cache: 'no-store',
      signal,
    });
    if (res.status === 401) {
      expireWidgetSession();
    }
    if (!res.ok) {
      const retryAfterSec = Number(res.headers.get('Retry-After'));
      throw Object.assign(new Error(`Turn events poll failed: ${res.status}`), {
        status: res.status,
        ...(retryAfterSec > 0 ? { retryAfterMs: retryAfterSec * 1000 } : {}),
      });
    }
    return res.json();
  },

  confirmToolCall(confirmationToken: string, approved: boolean, toolArgs?: Record<string, unknown>): Promise<Response> {
    const url = `${getBaseUrl()}/webchat/chat/confirm`;
    const body: Record<string, unknown> = { confirmationToken, approved };
    if (toolArgs !== undefined) body.toolArgs = toolArgs;
    return fetch(url, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify(body),
    }).then((res) => {
      if (res.status === 401) {
        expireWidgetSession();
      }
      return res;
    });
  },

  submitFeedback(
    conversationId: string,
    messageId: string,
    rating: 'positive' | 'negative',
  ): Promise<{ status: string }> {
    return request('/webchat/feedback', {
      method: 'POST',
      body: JSON.stringify({ conversationId, messageId, rating }),
    });
  },

  captchaStart(): Promise<{ token: string; question: string }> {
    return request('/webchat/captcha/start', { method: 'POST' });
  },

  captchaVerify(token: string, answer: number): Promise<{ success: boolean }> {
    return request('/webchat/captcha/verify', {
      method: 'POST',
      body: JSON.stringify({ token, answer }),
    });
  },

  upload(file: File, signal?: AbortSignal): Promise<UploadResponse> {
    const widgetId = store.getState().config.widgetId;
    const qs = widgetId ? `?widgetId=${encodeURIComponent(widgetId)}` : '';
    const url = `${getBaseUrl()}/webchat/upload${qs}`;
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    const token = store.getState().auth.token;
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData,
      signal,
    }).then(async (res) => {
      if (res.status === 401) {
        expireWidgetSession();
      }
      if (!res.ok) {
        let message = `Upload failed: ${res.status}`;
        try {
          const body = await res.json();
          if (body?.error?.message) message = body.error.message;
        } catch {
          // ignore parse errors
        }
        throw new Error(message);
      }
      return res.json();
    });
  },

  /** Ask the LLM to classify whether the text means "stop the conversation". */
  classifyIntent(text: string, conversationId?: string): Promise<{ isStop: boolean }> {
    const body: Record<string, unknown> = { text };
    const widgetId = store.getState().config.widgetId;
    if (widgetId) body.widgetId = widgetId;
    if (conversationId) body.conversationId = conversationId;
    return request<{ isStop: boolean }>('/webchat/classify-intent', {
      method: 'POST',
      body: JSON.stringify(body),
    }).catch(() => ({ isStop: false }));
  },

  /** Send raw audio blob to Whisper on the server and return the transcript. */
  async transcribeAudio(
    blob: Blob,
    language = 'he',
    context = '',
    conversationId?: string,
  ): Promise<{ transcript: string }> {
    const widgetId = store.getState().config.widgetId;
    const params = new URLSearchParams({ language });
    if (widgetId) params.set('widgetId', widgetId);
    if (conversationId) params.set('conversationId', conversationId);
    if (context) params.set('context', context.trim().slice(-300));
    const url = `${getBaseUrl()}/webchat/stt/transcribe?${params}`;

    const formData = new FormData();
    // Match the filename extension to the browser-chosen MIME type.
    const mime = blob.type || 'audio/webm';
    const ext = mime.includes('ogg')
      ? 'ogg'
      : mime.includes('mp4') || mime.includes('aac')
        ? 'mp4'
        : mime.includes('wav')
          ? 'wav'
          : 'webm';
    formData.append('audio', blob, `audio.${ext}`);

    const headers: Record<string, string> = {};
    const token = store.getState().auth.token;
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: formData,
    });

    if (res.status === 401) {
      expireWidgetSession('Session expired.');
    }

    if (!res.ok) {
      const bodyText = await res.text().catch(() => '');
      let parsedBody: { code?: number; message?: string } | null = null;

      if (bodyText) {
        try {
          parsedBody = JSON.parse(bodyText) as { code?: number; message?: string };
        } catch {
          parsedBody = null;
        }
      }

      const message = parsedBody?.message || bodyText || `STT failed: ${res.status}`;
      const error = new Error(message) as SttApiError;
      error.status = res.status;
      error.code = parsedBody?.code;
      error.responseMessage = message;
      throw error;
    }

    return res.json();
  },

  /** Text-to-Speech via server (uses OpenAI TTS). Returns audio blob. */
  async tts(text: string, language = 'he', voice = 'nova'): Promise<Blob> {
    const widgetId = store.getState().config.widgetId;
    const qs = widgetId ? `?widgetId=${encodeURIComponent(widgetId)}` : '';
    const url = `${getBaseUrl()}/webchat/tts${qs}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ text, language, voice }),
    });

    if (res.status === 401) {
      expireWidgetSession();
    }

    if (!res.ok) {
      const errBody = await res.text().catch(() => '');
      throw new Error(`TTS failed: ${res.status} - ${errBody}`);
    }

    return res.blob();
  },
};
